/**
 * Shared building blocks for the funnel stage books (SOW, Briefed…):
 * timing chain, quoted→signed variance panel, filter chips, sort
 * buttons and small aggregation helpers.
 */
import { Fragment } from "react";
import { ArrowDown, ArrowUp, ArrowRight, ChevronDown, X, Zap, Turtle, Timer } from "lucide-react";
import { PanelTitle, fmtGbp, fmtGbpShort, fmtDate } from "./CommissionedPanels";

export const median = (vals) => {
  if (!vals.length) return null;
  const s = [...vals].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round(((s[mid - 1] + s[mid]) / 2) * 10) / 10;
};

export const stat = (vals) => ({
  n: vals.length,
  avg: vals.length ? vals.reduce((s, d) => s + d, 0) / vals.length : null,
  median: median(vals),
});

export const groupAgg = (items, keyFn) => {
  const g = new Map();
  for (const i of items) {
    const k = keyFn(i);
    const b = g.get(k) || { count: 0, value: 0 };
    b.count += 1;
    b.value += i.value_gbp || 0;
    g.set(k, b);
  }
  return [...g.entries()].map(([k, v]) => ({ key: k, ...v }));
};

export const fmtDelta = (v) => `${v > 0 ? "+" : "−"}${fmtGbpShort(Math.abs(v))}`;

export function SortBtn({ label, k, sortKey, sortDir, onSort, testid }) {
  const active = sortKey === k;
  const Arrow = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      data-testid={testid}
      className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${active ? "text-[#1B4F8A] font-semibold" : "text-stone-400 hover:text-stone-600"}`}
    >
      {label}
      <Arrow className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
    </button>
  );
}

const CHIP_TONES = {
  violet: { chip: "text-violet-900 bg-violet-50 border-violet-200", hover: "hover:bg-violet-100" },
  indigo: { chip: "text-indigo-900 bg-indigo-50 border-indigo-200", hover: "hover:bg-indigo-100" },
  sky: { chip: "text-sky-900 bg-sky-50 border-sky-200", hover: "hover:bg-sky-100" },
  emerald: { chip: "text-emerald-900 bg-emerald-50 border-emerald-200", hover: "hover:bg-emerald-100" },
  amber: { chip: "text-amber-900 bg-amber-50 border-amber-200", hover: "hover:bg-amber-100" },
  teal: { chip: "text-teal-900 bg-teal-50 border-teal-200", hover: "hover:bg-teal-100" },
};

export function FilterChip({ label, onClear, testid, tone = "violet" }) {
  const t = CHIP_TONES[tone] || CHIP_TONES.violet;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-xs font-medium border rounded-full pl-3 pr-1.5 py-1 ${t.chip}`}
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        data-testid={`${testid}-clear`}
        className={`rounded-full p-0.5 transition-colors ${t.hover}`}
        title="Remove filter"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

export function TimingChain({ title, stages, legs, total, fastest, slowest, idPrefix, accent = "#7C3AED" }) {
  return (
    <div data-testid={`${idPrefix}-stage-timing`} className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>{title}</PanelTitle>
      <div className={`flex items-center ${stages.length >= 5 ? "gap-1" : "gap-1.5"}`}>
        {stages.map((s, i) => (
          <Fragment key={s}>
            {i > 0 && (
              <span className={`flex-1 flex flex-col items-center ${stages.length >= 5 ? "min-w-[26px]" : "min-w-[36px]"}`} data-testid={legs[i - 1]?.testid}>
                <span className={`${stages.length >= 5 ? "text-xs" : "text-sm"} font-semibold tabular-nums leading-none`} style={{ color: accent }}>
                  {legs[i - 1]?.days != null ? `${Math.round(legs[i - 1].days)}d` : "-"}
                </span>
                <span className="w-full flex items-center text-stone-300">
                  <span className="flex-1 border-t border-dashed border-stone-300" />
                  <ArrowRight className="w-3 h-3 shrink-0" />
                </span>
                <span className="text-[9px] font-mono text-stone-400">median</span>
              </span>
            )}
            <span className={`${stages.length >= 5 ? "px-1 py-0.5 text-[9px]" : "px-2 py-1 text-[10px]"} rounded-md bg-stone-100 font-mono uppercase tracking-wide text-slate-700 shrink-0`}>
              {s}
            </span>
          </Fragment>
        ))}
      </div>
      <div className="mt-3 flex items-end gap-4">
        <div>
          <p className="text-2xl font-medium tabular-nums text-[#1B4F8A] leading-none" data-testid={`${idPrefix}-timing-total`}>
            {total?.median != null ? Math.round(total.median) : "-"}
            <span className="text-sm font-normal text-stone-400 ml-1">days</span>
          </p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mt-1">median end-to-end</p>
        </div>
        <div>
          <p className="text-base font-medium tabular-nums text-slate-700 leading-none">
            {total?.avg != null ? Math.round(total.avg) : "-"}
            <span className="text-xs font-normal text-stone-400 ml-1">days</span>
          </p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mt-1">average</p>
        </div>
      </div>
      <div className="mt-3 space-y-1.5 text-xs">
        {fastest && (
          <p className="flex items-center gap-1.5 text-stone-600" data-testid={`${idPrefix}-timing-fastest`}>
            <Zap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span className="truncate">Fastest: <span className="font-medium text-slate-800">{fastest.name || "-"}</span></span>
            <span className="ml-auto font-mono tabular-nums shrink-0">{fastest.days}d</span>
          </p>
        )}
        {slowest && (
          <p className="flex items-center gap-1.5 text-stone-600" data-testid={`${idPrefix}-timing-slowest`}>
            <Turtle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span className="truncate">Slowest: <span className="font-medium text-slate-800">{slowest.name || "-"}</span></span>
            <span className="ml-auto font-mono tabular-nums shrink-0">{slowest.days}d</span>
          </p>
        )}
        <p className="text-[10px] text-stone-400 pt-1 flex items-center gap-1">
          <Timer className="w-3 h-3" /> based on {total?.n ?? 0} project{(total?.n ?? 0) === 1 ? "" : "s"} with all dates
        </p>
      </div>
    </div>
  );
}

export function ValueVariance({ variance, idPrefix = "sow", accentClass = "text-violet-700" }) {
  const { signed, current, delta, pct, movers, n } = variance;
  const maxAbs = Math.max(...movers.map((m) => Math.abs(m.value_delta)), 1);
  return (
    <div data-testid={`${idPrefix}-value-variance`} className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>Signed → current value</PanelTitle>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-lg font-medium tabular-nums text-slate-700" title="Agreed at signature (SOW VALUE £)">{fmtGbpShort(signed)}</span>
        <ArrowRight className="w-3.5 h-3.5 text-stone-300 shrink-0" />
        <span className={`text-lg font-medium tabular-nums ${accentClass}`} title="Live commercial figure (TO £)">{fmtGbpShort(current)}</span>
        <span
          data-testid={`${idPrefix}-variance-total`}
          className={`text-xs font-semibold font-mono px-2 py-0.5 rounded-full border ${
            delta > 0
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : delta < 0
              ? "text-rose-700 bg-rose-50 border-rose-200"
              : "text-stone-500 bg-stone-50 border-stone-200"
          }`}
        >
          {delta === 0 ? "±0" : fmtDelta(delta)}
          {pct != null && delta !== 0 && ` (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`}
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {movers.map((m) => (
          <li key={m.cid} data-testid={`${idPrefix}-variance-mover-${m.cid}`}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-medium text-slate-800 truncate max-w-[60%]">{m.client_company || "Unknown"}</span>
              <span className={`font-mono tabular-nums ${m.value_delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {fmtDelta(m.value_delta)}
                {m.value_delta_pct != null && ` (${m.value_delta_pct > 0 ? "+" : ""}${m.value_delta_pct}%)`}
              </span>
            </div>
            <div className="relative h-1.5 rounded-full bg-stone-100">
              <span className="absolute left-1/2 top-0 h-full w-px bg-stone-300" />
              <span
                className={`absolute top-0 h-full rounded-full ${m.value_delta > 0 ? "bg-emerald-500" : "bg-rose-500"}`}
                style={
                  m.value_delta > 0
                    ? { left: "50%", width: `${(Math.abs(m.value_delta) / maxAbs) * 48}%` }
                    : { right: "50%", width: `${(Math.abs(m.value_delta) / maxAbs) * 48}%` }
                }
              />
            </div>
          </li>
        ))}
        {movers.length === 0 && (
          <li className="text-xs text-stone-400" data-testid={`${idPrefix}-variance-no-movers`}>
            No value movement since signature in this view.
          </li>
        )}
      </ul>
      <p className="text-[10px] text-stone-400 mt-2">
        based on {n} project{n === 1 ? "" : "s"} with both figures · SOW VALUE (£) frozen at signature vs live TO (£)
      </p>
    </div>
  );
}

export function InvoiceToggleCell({ item, expanded, onToggle, idPrefix }) {
  const total = item.expected_invoices ?? (item.invoices || []).length;
  return (
    <td className="px-3 py-2.5 text-right whitespace-nowrap">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${idPrefix}-row-invoices-${item.cid}`}
        title={expanded ? "Hide invoice schedule" : "Show invoice schedule"}
        className="inline-flex items-center gap-1 font-mono tabular-nums text-xs text-slate-700 hover:text-[#1B4F8A] transition-colors"
      >
        {(item.overdue_invoices || 0) > 0 && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"
            title={`${item.overdue_invoices} overdue`}
            data-testid={`${idPrefix}-row-invoices-overdue-${item.cid}`}
          />
        )}
        {item.invoiced_count ?? 0}/{total || "?"}
        <ChevronDown className={`w-3.5 h-3.5 text-stone-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
    </td>
  );
}

export function InvoiceSchedule({ invoices, expected, idPrefix, cid }) {
  const today = new Date().toISOString().slice(0, 10);
  const rows = invoices || [];
  return (
    <div
      data-testid={`${idPrefix}-invoice-schedule-${cid}`}
      className="rounded-lg border border-stone-200 bg-white px-4 py-3 max-w-xl"
    >
      <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-2">
        Invoice schedule{expected ? ` · ${expected} expected` : ""}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-stone-400">No invoice schedule recorded on the Schedule of Work.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
              <th className="py-1 text-left font-medium">#</th>
              <th className="py-1 text-right font-medium">Date</th>
              <th className="py-1 text-right font-medium">Value</th>
              <th className="py-1 text-right font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => {
              const overdue = !inv.ref && inv.date && inv.date.slice(0, 10) < today;
              return (
                <tr key={inv.n} className="border-b border-stone-50 last:border-0">
                  <td className="py-1.5 font-mono text-stone-400">{inv.n}</td>
                  <td className="py-1.5 text-right font-mono tabular-nums text-stone-600 whitespace-nowrap">
                    {fmtDate(inv.date)}
                  </td>
                  <td className="py-1.5 text-right font-mono tabular-nums text-slate-800">
                    {inv.value != null ? fmtGbp(inv.value) : <span className="text-stone-300">-</span>}
                  </td>
                  <td className="py-1.5 text-right">
                    {inv.ref ? (
                      <span
                        className="inline-block text-[10px] font-mono px-1.5 py-0.5 rounded border text-emerald-800 bg-emerald-50 border-emerald-200"
                        title={`Issued · ref ${inv.ref}`}
                      >
                        {inv.ref}
                      </span>
                    ) : overdue ? (
                      <span className="inline-block text-[10px] font-mono px-1.5 py-0.5 rounded border text-rose-700 bg-rose-50 border-rose-200">
                        overdue
                      </span>
                    ) : (
                      <span className="inline-block text-[10px] font-mono px-1.5 py-0.5 rounded border text-stone-500 bg-stone-50 border-stone-200">
                        scheduled
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

